import {GitHubAvatar} from '@github-ui/github-avatar'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQueryErrorResetBoundary, useSuspenseQuery} from '@github-ui/react-query'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {ActionList, ActionMenu, Label, RelativeTime} from '@primer/react'
import type {ReactNode, RefObject} from 'react'
import {Suspense, useRef, useState} from 'react'

import {CouldNotFindFallbackError} from './components/CouldNotFindFallbackError'
import {EditHistoryDialog} from './components/EditHistoryDialog'
import {LABELS} from './constants/labels'
import {commentEditHistoryQueryKey, fetchCommentEditHistory} from './fetch-comment-edit-history'
import styles from './MarkdownEditHistoryViewer.module.css'

type IssueSecondaryNode = NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>

export type MarkdownEditHistoryViewerCommentFragment = Pick<
  IssueSecondaryNode,
  'id' | 'viewerCanReadUserContentEdits' | 'lastEditedAt'
>

type MarkdownEditHistoryViewerProps = {
  editHistory?: MarkdownEditHistoryViewerCommentFragment
  renderAnchor?: () => ReactNode
  anchorRef?: RefObject<HTMLButtonElement | null>
}

export function MarkdownEditHistoryViewer({
  editHistory: data,
  renderAnchor,
  anchorRef,
}: MarkdownEditHistoryViewerProps) {
  const [isEditHistoryDialogOpen, setIsEditHistoryDialogOpen] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const defaultAnchorRef = useRef<HTMLButtonElement>(null)
  const historyButtonRef = anchorRef ?? defaultAnchorRef

  if (!data || !data.viewerCanReadUserContentEdits || !data.lastEditedAt) {
    return null
  }

  return (
    <div className={styles.editHistoryContainer}>
      <ActionMenu anchorRef={historyButtonRef}>
        <ActionMenu.Button
          variant="invisible"
          size="small"
          aria-label={renderAnchor ? undefined : LABELS.editHistory.ariaLabel}
          className={styles.editHistoryMenuButton}
        >
          {renderAnchor?.() ?? LABELS.editHistory.openEditsButton}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="auto">
          <EditHistoryActionMenuContent
            setIsEditHistoryDialogOpen={setIsEditHistoryDialogOpen}
            setSelectedId={setSelectedId}
            editHistoryId={data.id}
            lastEditedAt={data.lastEditedAt}
          />
        </ActionMenu.Overlay>
      </ActionMenu>
      {isEditHistoryDialogOpen && (
        <EditHistoryDialog
          onClose={() => setIsEditHistoryDialogOpen(false)}
          userContentEditId={selectedId}
          returnFocusRef={historyButtonRef}
        />
      )}
    </div>
  )
}

type EditHistoryActionMenuContentProps = {
  editHistoryId: string
  lastEditedAt: string
} & SharedProps

const EditHistoryActionMenuContent = ({editHistoryId, lastEditedAt, ...props}: EditHistoryActionMenuContentProps) => {
  const {reset} = useQueryErrorResetBoundary()
  const [retryKey, setRetryKey] = useState(0)
  const retry = () => {
    reset()
    setRetryKey(key => key + 1)
  }

  return (
    <ErrorBoundary
      key={retryKey}
      boundaryName="MarkdownEditHistoryViewer"
      fallback={<CouldNotFindFallbackError retry={retry} />}
    >
      <Suspense fallback={<EditHistoryLoading />}>
        <EditHistoryActionMenuContentInternal editHistoryId={editHistoryId} lastEditedAt={lastEditedAt} {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

type EditHistoryActionMenuContentInternalProps = {
  editHistoryId: string
  lastEditedAt: string
} & SharedProps

type SharedProps = {
  setIsEditHistoryDialogOpen: (isOpen: boolean) => void
  setSelectedId: (id: string) => void
}

const EditHistoryActionMenuContentInternal = ({
  setIsEditHistoryDialogOpen,
  setSelectedId,
  editHistoryId,
  lastEditedAt,
}: EditHistoryActionMenuContentInternalProps) => {
  const {data} = useSuspenseQuery({
    // `lastEditedAt` is part of the key so a new edit refetches the history.
    queryKey: [...commentEditHistoryQueryKey(editHistoryId), lastEditedAt],
    queryFn: ({signal}) => fetchCommentEditHistory(editHistoryId, signal),
  })

  const totalCount = data.userContentEdits.totalCount

  return (
    <ActionList className={styles.scrollableEditHistoryList}>
      <ActionList.Group>
        <ActionList.GroupHeading>{`Edited ${totalCount - 1} time${totalCount > 2 ? 's' : ''}`}</ActionList.GroupHeading>
        {data.userContentEdits.edges?.map((edge, index) => {
          const edit = edge?.node
          if (!edit?.editor) {
            return null
          }

          const isCreation = data.includesCreatedEdit && index === totalCount - 1

          return (
            <ActionList.Item
              key={edit.id}
              onSelect={() => {
                setSelectedId(edit.id)
                setIsEditHistoryDialogOpen(true)
              }}
            >
              <ActionList.LeadingVisual>
                <GitHubAvatar src={edit.editor.avatarUrl} size={16} alt={`@${edit.editor.login}`} />
              </ActionList.LeadingVisual>
              <span className={styles.editorUsername}>{edit.editor.login}</span>
              <ActionList.Description truncate className={styles.editHistoryItemMetadata}>
                {isCreation && <div title="">{LABELS.editHistory.created}</div>}
                <RelativeTime date={new Date(edit.editedAt)} />
              </ActionList.Description>
              {(index === 0 || edit.deletedAt) && (
                <ActionList.TrailingVisual>
                  <Label>{index === 0 ? LABELS.editHistory.mostRecent : LABELS.editHistory.deleted}</Label>
                </ActionList.TrailingVisual>
              )}
            </ActionList.Item>
          )
        })}
      </ActionList.Group>
    </ActionList>
  )
}

function EditHistoryLoading() {
  return (
    <div className={styles.loadingSkeletonContainer}>
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
      <LoadingSkeleton variant="rounded" height="md" width="100%" />
    </div>
  )
}
